import { nextAvailableAquiferName, type Aquifer } from "../model/inp/aquifers.ts";
import type { Pattern } from "../model/inp/patterns.ts";
import { useT } from "../i18n/hooks.ts";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";
import { NumberInput } from "./fields.tsx";

export function AquifersEditor({
  aquifers,
  onChange,
  selectedName,
  onSelect,
  patterns,
}: {
  aquifers: Aquifer[];
  onChange: (aquifers: Aquifer[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
  patterns: Pattern[];
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteAquifer,
    rename: renameSelected,
    update,
  } = useNamedCollection(aquifers, onChange, selectedName, onSelect);

  function addAquifer() {
    const name = nextAvailableAquiferName(new Set(aquifers.map((a) => a.name)));
    add({
      name,
      porosity: 0,
      wiltingPoint: 0,
      fieldCapacity: 0,
      conductivity: 0,
      conductSlope: 0,
      tensionSlope: 0,
      upperEvapFrac: 0,
      lowerEvapDepth: 0,
      gwRecession: 0,
      bottomElev: 0,
      waterTableElev: 0,
      upperMoisture: 0,
      evapPattern: "",
    });
  }

  function set<K extends keyof Aquifer>(key: K, value: Aquifer[K]) {
    update((a) => ({ ...a, [key]: value }));
  }

  const NUMBER_FIELDS: { key: keyof Aquifer; labelKey: Parameters<typeof t>[0] }[] = [
    { key: "porosity", labelKey: "aquifer.porosity" },
    { key: "wiltingPoint", labelKey: "aquifer.wiltingPoint" },
    { key: "fieldCapacity", labelKey: "aquifer.fieldCapacity" },
    { key: "conductivity", labelKey: "aquifer.conductivity" },
    { key: "conductSlope", labelKey: "aquifer.conductSlope" },
    { key: "tensionSlope", labelKey: "aquifer.tensionSlope" },
    { key: "upperEvapFrac", labelKey: "aquifer.upperEvapFrac" },
    { key: "lowerEvapDepth", labelKey: "aquifer.lowerEvapDepth" },
    { key: "gwRecession", labelKey: "aquifer.gwRecession" },
    { key: "bottomElev", labelKey: "aquifer.bottomElev" },
    { key: "waterTableElev", labelKey: "aquifer.waterTableElev" },
    { key: "upperMoisture", labelKey: "aquifer.upperMoisture" },
  ];

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={aquifers}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addAquifer}
        addLabel={t("aquifer.addAquifer")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("aquifer.noAquiferSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteAquifer(selected.name)}
              deleteLabel={t("aquifer.deleteAquifer")}
            />
            <div className="named-collection-fields">
              {NUMBER_FIELDS.map((field) => (
                <label className="options-field" key={field.key}>
                  {t(field.labelKey)}
                  <NumberInput
                    value={selected[field.key] as number}
                    onChange={(v) => set(field.key, v as Aquifer[typeof field.key])}
                  />
                </label>
              ))}
              <label className="options-field">
                {t("aquifer.evapPattern")}
                <select
                  value={selected.evapPattern}
                  onChange={(e) => set("evapPattern", e.target.value)}
                >
                  <option value="">{t("aquifer.evapPatternNone")}</option>
                  {patterns.map((p) => (
                    <option key={p.name} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
